import { useState, useMemo } from 'react';
import { Copy, Pipette } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { copyToClipboard, hexToRgb, rgbToHex, rgbToHsl, hslToRgb } from '@/lib/utils';
import { toast } from 'sonner';

export function ColorConverter() {
  const [hex, setHex] = useState('#6366f1');

  const rgb = useMemo(() => hexToRgb(hex) ?? { r: 99, g: 102, b: 241 }, [hex]);
  const hsl = useMemo(() => rgbToHsl(rgb.r, rgb.g, rgb.b), [rgb]);

  function fromRgb(r: number, g: number, b: number) {
    setHex(rgbToHex(r, g, b));
  }

  function fromHsl(h: number, s: number, l: number) {
    const { r, g, b } = hslToRgb(h, s, l);
    setHex(rgbToHex(r, g, b));
  }

  const css = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  const hslCss = `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
  const cssVar = `--brand: ${hex};`;

  const formats = [
    { label: 'HEX', value: hex.toUpperCase() },
    { label: 'RGB', value: css },
    { label: 'HSL', value: hslCss },
    { label: 'CSS variable', value: cssVar },
    { label: 'Tailwind arbitrary', value: `bg-[${hex}]` },
    { label: 'Swift UI', value: `Color(red: ${(rgb.r / 255).toFixed(3)}, green: ${(rgb.g / 255).toFixed(3)}, blue: ${(rgb.b / 255).toFixed(3)})` },
  ];

  return (
    <div>
      <ToolHeader
        toolId="color-picker"
        title="Color picker & converter"
        description="Pick a color and read it as HEX, RGB, HSL, CSS, Tailwind, or SwiftUI."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="!p-0 overflow-hidden">
          <div
            className="h-48 transition-colors"
            style={{ backgroundColor: hex }}
          />
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                className="!w-12 !h-9"
              />
              <Input
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
        </Card>

        <Card className="space-y-4">
          <div>
            <Field label="R" hint={String(rgb.r)}>
              <Slider
                value={rgb.r}
                onChange={(v) => fromRgb(v, rgb.g, rgb.b)}
                max={255}
                showValue={false}
              />
            </Field>
            <Field label="G" hint={String(rgb.g)}>
              <Slider
                value={rgb.g}
                onChange={(v) => fromRgb(rgb.r, v, rgb.b)}
                max={255}
                showValue={false}
              />
            </Field>
            <Field label="B" hint={String(rgb.b)}>
              <Slider
                value={rgb.b}
                onChange={(v) => fromRgb(rgb.r, rgb.g, v)}
                max={255}
                showValue={false}
              />
            </Field>
          </div>
          <div className="border-t border-border pt-3">
            <Field label="H" hint={String(hsl.h)}>
              <Slider
                value={hsl.h}
                onChange={(v) => fromHsl(v, hsl.s, hsl.l)}
                max={360}
                showValue={false}
              />
            </Field>
            <Field label="S" hint={`${hsl.s}%`}>
              <Slider
                value={hsl.s}
                onChange={(v) => fromHsl(hsl.h, v, hsl.l)}
                max={100}
                showValue={false}
              />
            </Field>
            <Field label="L" hint={`${hsl.l}%`}>
              <Slider
                value={hsl.l}
                onChange={(v) => fromHsl(hsl.h, hsl.s, v)}
                max={100}
                showValue={false}
              />
            </Field>
          </div>
        </Card>
      </div>

      <Card className="space-y-2">
        <div className="text-xs font-medium text-fg-muted mb-1">Formats</div>
        {formats.map((f) => (
          <div
            key={f.label}
            className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-bg-inset border border-border hover:border-border-strong transition group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-32 shrink-0 text-2xs uppercase tracking-wider text-fg-subtle font-mono">
                {f.label}
              </div>
              <div className="font-mono text-sm truncate">{f.value}</div>
            </div>
            <button
              onClick={async () => {
                const ok = await copyToClipboard(f.value);
                if (ok) toast.success('Copied');
              }}
              className="opacity-0 group-hover:opacity-100 transition size-7 rounded hover:bg-surface-hover text-fg-subtle hover:text-fg flex items-center justify-center shrink-0"
            >
              <Copy size={11} />
            </button>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default ColorConverter;
